import React from 'react';
import Icon from '../../../components/AppIcon';

const QRAnalytics = ({ qrCodes }) => {
  const activeQRs = qrCodes?.filter(qr => qr?.is_active) || [];
  const totalScans = qrCodes?.reduce((sum, qr) => sum + (qr?.scan_count || 0), 0);
  
  const qrBySource = {
    member: qrCodes?.filter(qr => qr?.source === 'member')?.length || 0,
    staff: qrCodes?.filter(qr => qr?.source === 'staff')?.length || 0
  };

  const topScannedQRs = [...qrCodes]
    ?.sort((a, b) => (b?.scan_count || 0) - (a?.scan_count || 0))
    ?.slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg shadow-md p-6 border border-border">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="QrCode" size={24} className="text-blue-500" />
            <h3 className="font-bold text-foreground">QR Code Distribution</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Member QR Codes</span>
              <span className="text-lg font-bold text-foreground">{qrBySource?.member}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Staff QR Codes</span>
              <span className="text-lg font-bold text-foreground">{qrBySource?.staff}</span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-md p-6 border border-border">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="CheckCircle" size={24} className="text-green-500" />
            <h3 className="font-bold text-foreground">Active Status</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Active QR Codes</span>
              <span className="text-lg font-bold text-green-500">{activeQRs?.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Inactive QR Codes</span>
              <span className="text-lg font-bold text-gray-500">{qrCodes?.length - activeQRs?.length}</span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-md p-6 border border-border">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="ScanLine" size={24} className="text-purple-500" />
            <h3 className="font-bold text-foreground">Scan Activity</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total Scans</span>
              <span className="text-lg font-bold text-purple-500">{totalScans}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Avg Scans/QR</span>
              <span className="text-lg font-bold text-foreground">
                {qrCodes?.length > 0 ? (totalScans / qrCodes?.length)?.toFixed(1) : 0}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Top Scanned QR Codes */}
      <div className="bg-card rounded-lg shadow-md p-6 border border-border">
        <div className="flex items-center gap-2 mb-6">
          <Icon name="TrendingUp" size={24} className="text-primary" />
          <h2 className="text-xl font-bold text-foreground">Top Scanned QR Codes</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Rank</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">User</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Scans</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {topScannedQRs?.map((qr, index) => (
                <tr key={qr?.id} className="border-b border-border hover:bg-background/50">
                  <td className="py-3 px-4">
                    <span className="text-lg font-bold text-primary">#{index + 1}</span>
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-foreground">{qr?.user_profiles?.full_name}</p>
                      <p className="text-xs text-muted-foreground">{qr?.user_profiles?.email}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      qr?.source === 'staff' ?'bg-blue-500/10 text-blue-500' :'bg-green-500/10 text-green-500'
                    }`}>
                      {qr?.source?.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-lg font-bold text-foreground">{qr?.scan_count || 0}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      qr?.is_active 
                        ? 'bg-green-500/10 text-green-500' :'bg-gray-500/10 text-gray-500'
                    }`}>
                      {qr?.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {topScannedQRs?.length === 0 && (
            <div className="text-center text-muted-foreground py-12">
              <Icon name="BarChart3" size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No Scan Data Available</p>
              <p className="text-sm mt-2">QR codes haven't been scanned yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRAnalytics;